/**
 * Goals and the composite Value Score (spec sections 14 & 15).
 *
 * The score is deliberately *not* a black box: `ValueScore.components` carries
 * every input, its weight, and its normalized sub-score so the UI can show the
 * full derivation. Section 14 also forbids health claims — this measures
 * economic and nutritional value per dollar, nothing about whether a food is
 * good for you.
 */

import type { MetricId } from './calculations';
import { METRIC_DIRECTION, METRICS, type ProductMetrics } from './calculations';

// ---------------------------------------------------------------------------
// Goals
// ---------------------------------------------------------------------------

export type GoalId =
  | 'max_protein'
  | 'max_calories'
  | 'lowest_cost'
  | 'protein_on_budget'
  | 'balanced';

export interface Goal {
  id: GoalId;
  label: string;
  question: string;
  /**
   * The metric this goal ranks by. `null` for `balanced`, which ranks by the
   * composite score instead.
   */
  primaryMetric: MetricId | null;
  /** Shown next to the goal so the underlying metric is never hidden (s.15). */
  explanation: string;
}

export const GOALS: Goal[] = [
  {
    id: 'max_protein',
    label: 'Maximum protein',
    question: 'Most protein for my money',
    primaryMetric: 'proteinPerDollar',
    explanation: 'Ranks by grams of protein per unit of currency, using package totals.',
  },
  {
    id: 'max_calories',
    label: 'Maximum calories',
    question: 'Most energy for my money',
    primaryMetric: 'kcalPerDollar',
    explanation: 'Ranks by kilocalories per unit of currency, using package totals.',
  },
  {
    id: 'lowest_cost',
    label: 'Lowest cost',
    question: 'Cheapest per unit of food',
    primaryMetric: 'pricePer100g',
    explanation:
      'Ranks by normalized price — per 100 g for solids, per 100 ml for liquids — not by sticker price.',
  },
  {
    id: 'protein_on_budget',
    label: 'Protein on a calorie budget',
    question: 'Most protein per calorie',
    primaryMetric: 'proteinPer100kcal',
    explanation:
      'Ranks by grams of protein per 100 kcal, for when calories are the constraint rather than money.',
  },
  {
    id: 'balanced',
    label: 'Balanced value',
    question: 'A blend of protein, energy and price',
    primaryMetric: null,
    explanation: 'Ranks by the composite Value Score — see the breakdown on each product.',
  },
];

export function goalById(id: GoalId): Goal {
  return GOALS.find((g) => g.id === id) ?? GOALS[GOALS.length - 1]!;
}

/**
 * The metric a goal actually sorts by, resolved against a product's package
 * kind so "lowest cost" uses price/100 ml for liquids.
 */
export function resolveGoalMetric(goal: Goal, kind: 'mass' | 'volume' | 'count' | null): MetricId | null {
  if (goal.primaryMetric === 'pricePer100g' && kind === 'volume') return 'pricePer100ml';
  return goal.primaryMetric;
}

// ---------------------------------------------------------------------------
// Composite Value Score
// ---------------------------------------------------------------------------

export interface ScoreComponent {
  metric: MetricId;
  label: string;
  weight: number;
  /** The product's raw metric value. */
  raw: number | null;
  /** 0..1 position within the compared set. `null` when the metric is missing. */
  normalized: number | null;
  /** Populated when `normalized` is null. */
  unavailableReason: string | null;
}

export interface ValueScore {
  /** 0..100, or `null` when too little data exists to score honestly. */
  score: number | null;
  components: ScoreComponent[];
  /** Sum of the weights that actually contributed. */
  coverage: number;
  unavailableReason: string | null;
}

export interface ScoringProfile {
  id: string;
  label: string;
  weights: Partial<Record<MetricId, number>>;
  /**
   * Minimum fraction of total weight that must be available for a score to be
   * emitted. Prevents a product with one lucky metric outranking a complete one.
   */
  minCoverage: number;
}

export const DEFAULT_PROFILE: ScoringProfile = {
  id: 'balanced',
  label: 'Balanced value',
  weights: {
    proteinPerDollar: 0.4,
    kcalPerDollar: 0.2,
    pricePer100g: 0.25,
    proteinPer100kcal: 0.15,
  },
  minCoverage: 0.6,
};

export const PROFILES: ScoringProfile[] = [
  DEFAULT_PROFILE,
  {
    id: 'protein_focused',
    label: 'Protein focused',
    weights: { proteinPerDollar: 0.6, proteinPer100kcal: 0.25, pricePer100g: 0.15 },
    minCoverage: 0.6,
  },
  {
    id: 'energy_focused',
    label: 'Energy focused',
    weights: { kcalPerDollar: 0.6, pricePer100g: 0.4 },
    minCoverage: 0.6,
  },
];

/** Min/max range of one metric across the compared set. */
interface Range {
  min: number;
  max: number;
}

function rangesFor(
  all: ProductMetrics[],
  metrics: MetricId[],
): Map<MetricId, Range> {
  const out = new Map<MetricId, Range>();
  for (const metric of metrics) {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const m of all) {
      const v = m.values[metric]?.value;
      if (v == null || !Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (Number.isFinite(min) && Number.isFinite(max)) out.set(metric, { min, max });
  }
  return out;
}

/**
 * Score every product against the same set, so a score is always relative to
 * the products on screen. Scoring in isolation would be meaningless — "92" only
 * means something next to the alternatives.
 */
export function scoreAll(
  all: ProductMetrics[],
  profile: ScoringProfile = DEFAULT_PROFILE,
): Map<string, ValueScore> {
  const entries = Object.entries(profile.weights).filter(
    (e): e is [MetricId, number] => typeof e[1] === 'number' && e[1] > 0,
  );
  const totalWeight = entries.reduce((sum, [, w]) => sum + w, 0);
  const result = new Map<string, ValueScore>();
  if (totalWeight <= 0 || all.length === 0) return result;

  // "lowest cost" is metric-substituted per product, so resolve the price
  // metric each product actually uses before computing ranges.
  const usesVolume = all.some((m) => m.packageKind === 'volume');
  const resolveMetric = (metric: MetricId, m: ProductMetrics): MetricId => {
    if (metric === 'pricePer100g' && m.packageKind === 'volume') return 'pricePer100ml';
    return metric;
  };

  const metricIds = entries.map(([id]) => id);
  if (usesVolume && metricIds.includes('pricePer100g')) metricIds.push('pricePer100ml');
  const ranges = rangesFor(all, metricIds);

  for (const m of all) {
    const components: ScoreComponent[] = [];
    let weighted = 0;
    let coverage = 0;

    for (const [metric, weight] of entries) {
      const actual = resolveMetric(metric, m);
      const mv = m.values[actual];
      const range = ranges.get(actual);
      const meta = METRICS[actual];

      if (mv?.value == null || range == null) {
        components.push({
          metric: actual,
          label: meta.label,
          weight,
          raw: null,
          normalized: null,
          unavailableReason: mv?.unavailableReason ?? 'Not comparable across these results.',
        });
        continue;
      }

      // Min-max normalize into 0..1, flipping when lower is better.
      const span = range.max - range.min;
      let norm: number;
      if (span <= 0) {
        // Every product ties on this metric — it carries no ranking signal.
        norm = 1;
      } else {
        norm = (mv.value - range.min) / span;
        if (METRIC_DIRECTION[actual] === 'lower') norm = 1 - norm;
      }

      components.push({
        metric: actual,
        label: meta.label,
        weight,
        raw: mv.value,
        normalized: norm,
        unavailableReason: null,
      });
      weighted += norm * weight;
      coverage += weight;
    }

    const coverageFraction = coverage / totalWeight;
    if (coverageFraction < profile.minCoverage) {
      result.set(m.productId, {
        score: null,
        components,
        coverage: coverageFraction,
        unavailableReason:
          'Too much nutrition or price data is missing to score this product fairly.',
      });
      continue;
    }

    // Rescale by the weight that actually contributed, so a product is not
    // punished for a metric nobody in the set has.
    result.set(m.productId, {
      score: Math.round((weighted / coverage) * 100),
      components,
      coverage: coverageFraction,
      unavailableReason: null,
    });
  }

  return result;
}
